import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Menu, X, Shield } from 'lucide-react';

const navLinks = [
  { label: 'Leistungen', href: '#leistungen' },
  { label: 'Vorteile', href: '#vorteile' },
  { label: 'Prozess', href: '#prozess' },
  { label: 'FAQ', href: '/faq', isPage: true },
  { label: 'Kontakt', href: '#kontakt' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navigate = useNavigate();

  const handleNav = (href, isPage) => {
    setMobileOpen(false);
    if (isPage) {
      navigate(href);
    } else {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'glass shadow-lg shadow-black/[0.03]'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <button
            data-testid="navbar-logo"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="flex items-center gap-2.5 group"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-blue flex items-center justify-center shadow-lg shadow-blue-500/20 group-hover:shadow-blue-500/40 transition-shadow duration-300">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold tracking-tight font-['Manrope']" style={{ color: '#0a1628' }}>
              Equi<span className="gradient-text">Guard</span>
            </span>
          </button>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <button
                key={link.href}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                onClick={() => handleNav(link.href, link.isPage)}
                className="px-4 py-2 text-sm font-medium rounded-full transition-all duration-300 hover:bg-[#007aff]/[0.06] hover:text-[#007aff]"
                style={{ color: '#374151' }}
              >
                {link.label}
              </button>
            ))}
          </div>

          {/* CTA */}
          <div className="hidden lg:block">
            <Button
              data-testid="nav-cta-button"
              onClick={() => handleNav('#kontakt')}
              className="rounded-full px-6 h-11 text-sm font-semibold bg-gradient-cta hover:opacity-90 transition-opacity shadow-lg shadow-emerald-500/25"
              style={{ fontFamily: 'Manrope' }}
            >
              Erstprüfung anfordern
            </Button>
          </div>

          {/* Mobile Toggle */}
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 rounded-xl hover:bg-gray-100 transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div data-testid="mobile-menu" className="lg:hidden glass border-t border-gray-200/50">
          <div className="px-6 py-4 space-y-1">
            {navLinks.map((link) => (
              <button
                key={link.href}
                data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                onClick={() => handleNav(link.href, link.isPage)}
                className="block w-full text-left px-4 py-3 text-base font-medium rounded-xl hover:bg-[#007aff]/[0.06] hover:text-[#007aff] transition-colors"
                style={{ color: '#374151' }}
              >
                {link.label}
              </button>
            ))}
            <div className="pt-3">
              <Button
                data-testid="mobile-cta-button"
                onClick={() => handleNav('#kontakt')}
                className="w-full rounded-full h-12 text-base font-semibold bg-gradient-blue shadow-lg shadow-blue-500/25"
                style={{ fontFamily: 'Manrope' }}
              >
                Erstprüfung anfordern
              </Button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
